import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowDownRight, Flame } from "lucide-react";
import { scrollToSection } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHxfHx5b3V0aCUyMHNwb3J0cyUyMGJhc2tldGJhbGwlMjBtZW50b3JpbmclMjBkYXJrJTIwZHJhbWF0aWN8ZW58MHx8fHwxNzg3NDE3MDA4fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["GRIND.", "ACHIEVE.", "MOTIVATE.", "EXCEL."];
const PILLARS = ["Mentorship", "Academics", "Athletics", "Wellness", "Leadership", "College & Career"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Youth athlete mid-dunk under arena lights" className="w-full h-[120%] object-cover object-center opacity-75" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080C] via-[#08080C]/55 to-[#08080C]/20" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 50% at 72% 28%, rgba(139,92,246,0.25), transparent 60%)" }} />
        <div className="absolute inset-0 bg-grid" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 px-4 sm:px-8 lg:px-16 pb-10 pt-40 w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="bg-[#8B5CF6] text-black font-mono font-bold px-3 py-1 text-xs uppercase tracking-widest" data-testid="hero-badge">
            G.A.M.E. Mentorship & Training Inc.
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#A1A1B3] hidden sm:block">
            Founded by Jeffery Mizell
          </span>
        </motion.div>

        <h1 className="font-heading font-black uppercase tracking-tight leading-[0.92] text-4xl sm:text-6xl lg:text-8xl text-[#F4F4F6]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-outline-kinetic" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.14, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-6 max-w-xl"
          data-testid="hero-subtext"
        >
          <span className="block font-mono text-xs uppercase tracking-[0.25em] text-[#FFB800] mb-3">
            Training. Mentorship. Education. Performance. Lifestyle.
          </span>
          <span className="block text-sm sm:text-base leading-relaxed text-[#A1A1B3]">
            We empower young people through mentorship, academic enrichment, athletic development,
            health & wellness, and leadership training — building confidence and opening pathways
            to success in education, careers, and life.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-8 flex flex-wrap gap-4"
        >
          <button
            onClick={() => scrollToSection("#donate")}
            data-testid="hero-donate-button"
            className="bg-[#8B5CF6] text-black font-display font-extrabold uppercase tracking-wider text-base px-8 py-4 flex items-center gap-2 hover:bg-[#FFB800] active:scale-95 transition-colors duration-200"
          >
            <Flame className="w-5 h-5" strokeWidth={2.5} />
            Support the Mission
          </button>
          <button
            onClick={() => scrollToSection("#volunteer")}
            data-testid="hero-volunteer-button"
            className="border border-[#3D3D58] text-[#F4F4F6] font-display font-extrabold uppercase tracking-wider text-base px-8 py-4 flex items-center gap-2 hover:border-[#8B5CF6] hover:text-[#8B5CF6] active:scale-95 transition-colors duration-200"
          >
            Become a Mentor
            <ArrowDownRight className="w-5 h-5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 border-t border-[#242436] pt-8"
          data-testid="hero-pillars"
        >
          {PILLARS.map((p) => (
            <div key={p} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#FFB800] inline-block shrink-0" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#A1A1B3]">{p}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
